import { EntityType, Prisma } from "@prisma/client";
import { z } from "zod";

/** Shared with the Inbox list (`inbox.service.ts`) so a TASK row hydrates the same shape `TaskCard` renders. */
export type TaskWithAssignedUser = Prisma.TaskGetPayload<{
  include: {
    assignedTo: true;
    createdBy: true;
  };
}>;

const taskIdSchemaKey = { taskId: z.string().uuid() };

export const retrieveForEntitySchema = z.object({
  entityId: z.string().uuid(),
  entityType: z.nativeEnum(EntityType),
});
export type RetrieveForEntitySchemaType = z.infer<typeof retrieveForEntitySchema>;

export const retrieveAssignedToUserSchema = z.object({
  userId: z.string().uuid(),
});
export type RetrieveAssignedToUserSchemaType = z.infer<typeof retrieveAssignedToUserSchema>;

export const completeTaskSchema = z.object({
  isCompleted: z.boolean().default(true),
  taskId: z.string().uuid(),
  completedNotes: z.string().optional(),
});
export type CompleteTaskSchemaType = z.infer<typeof completeTaskSchema>;

export const assignTaskSchema = z.object({
  ...taskIdSchemaKey,
  assignedToId: z.string().uuid(),
});
export type AssignTaskSchemaType = z.infer<typeof assignTaskSchema>;

export const upsertTaskSchemaBase = z.object({
  assignedToId: z.string().uuid().nullish(),
  title: z.string().trim().min(1, "Title is required"),
  description: z.string().nullish(),
  dueDate: z.coerce.date().nullish(),
});
export type UpsertTaskSchemaBaseType = z.infer<typeof upsertTaskSchemaBase>;

export const updateTaskSchema = upsertTaskSchemaBase.extend({
  ...taskIdSchemaKey,
});
export type UpdateTaskSchemaType = z.infer<typeof updateTaskSchema>;

export const createTaskSchema = upsertTaskSchemaBase.extend({
  entityId: z.string().uuid().nullish(),
  entityType: z.nativeEnum(EntityType).nullish(),
});
export type CreateTaskSchemaType = z.infer<typeof createTaskSchema>;

export const retrieveTasksSchema = z.object({
  entityId: z.string().uuid().optional(),
  entityType: z.nativeEnum(EntityType).optional(),
  includeCompleted: z.boolean().nullish().default(false),
  onlyMine: z.boolean().nullish().default(false),
});
export type RetrieveTasksSchemaType = z.infer<typeof retrieveTasksSchema>;

export const deleteTaskSchema = z.object({
  ...taskIdSchemaKey,
});
export type DeleteTaskSchemaType = z.infer<typeof deleteTaskSchema>;
